import React from 'react';
import { motion } from 'framer-motion';
import { Play, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function HeroSection({ onBookDemo }) {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[90vh] flex flex-col justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1764258559605-5b769b04550e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdsb3dpbmclMjB3YXZlcyUyMHRlY2glMjBkYXJrfGVufDB8fHx8MTc3NDkzMzI3MHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-black/70" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 lg:py-0">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="max-w-3xl"
        >
          {/* Label */}
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="inline-block text-xs uppercase tracking-[0.2em] text-[#00E5FF] font-semibold mb-6"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Fully Managed AI Voice Agents for Real Estate
          </motion.span>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-medium text-white leading-[1.05] mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Never Miss a Lead Again.{' '}
            <span className="text-neutral-500">AI Voice Calling for Calgary Real Estate.</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="text-base lg:text-lg leading-relaxed text-neutral-400 mb-10 max-w-2xl"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Speed to lead is everything. We build and deploy custom AI voice agents that
            answer, qualify, and book your real estate leads 24/7 — fully managed, so you can focus on closing deals.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Button
              data-testid="hero-book-demo-btn"
              onClick={onBookDemo}
              className="bg-[#00E5FF] text-black font-bold hover:bg-[#33EFFF] px-8 py-6 rounded-full text-base transition-all duration-200 pulse-cta flex items-center gap-2"
            >
              Book a Demo
              <ArrowRight size={18} />
            </Button>
            <Button
              data-testid="hero-see-action-btn"
              id="see-it-in-action-btn"
              className="border border-white/30 bg-transparent text-white hover:bg-white/10 hover:border-white/50 px-8 py-6 rounded-full text-base transition-all duration-200 flex items-center gap-2"
            >
              <Play size={16} />
              See It In Action
            </Button>
          </motion.div>

          {/* Social proof */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-14 flex items-center gap-6"
          >
            <div className="flex -space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-8 h-8 rounded-full bg-neutral-800 border-2 border-[#050505] flex items-center justify-center text-[10px] text-neutral-400"
                >
                  {['JM', 'SK', 'AL', 'RB'][i - 1]}
                </div>
              ))}
            </div>
            <p className="text-sm text-neutral-500">
              Trusted by <span className="text-white font-medium">50+</span> Calgary agents
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
